import { useMemo, type FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import type { GetUnrecognizedAnswersQuery } from "api/generated";
import { examHistoryDate } from "features/ExamHistory/examHistoryDate";
import { ASSIGNMENT_RETURN_PATHS } from "utils/common/assignmentDetail";

import AnswerStatusPill from "./AnswerStatusPill";
import { getAnswerStatus } from "./answerStatus";
import { diffAnswers } from "./diffAnswers";
import HighlightedAnswer from "./HighlightedAnswer";

const [RETURN_PATH] = ASSIGNMENT_RETURN_PATHS;

export type UnrecognizedAnswerItem = GetUnrecognizedAnswersQuery["unrecognizedAnswers"]["items"][number];

interface Props {
  item: UnrecognizedAnswerItem;
  now: Date;
}

const UnrecognizedAnswerRow: FC<Props> = ({ item, now }) => {
  const { t } = useTranslation("unrecognizedAnswers");
  const {
    date,
    assignmentId,
    userAssignmentId,
    testName,
    testOrder,
    questionNumber,
    studentAnswer,
    correctAnswer,
    checked,
    wasCorrect,
  } = item;

  // Pokus studenta i s jeho odpovědí; náhled úlohy jen u starých záznamů bez pokusu.
  const target = userAssignmentId
    ? `/ulohy/detail/${userAssignmentId}`
    : assignmentId
      ? `/ulohy-z-cms/${assignmentId}`
      : undefined;
  const diff = useMemo(() => diffAnswers(studentAnswer, correctAnswer), [studentAnswer, correctAnswer]);
  const label =
    testName && testOrder
      ? t("taskTitle", { test: testName, order: testOrder })
      : t("taskFallback", { number: questionNumber ?? "–" });

  return (
    <div className={target ? "unrecognized-answer-row unrecognized-answer-row--link" : "unrecognized-answer-row"}>
      <div className="unrecognized-answer-row__date">{date ? examHistoryDate(date, now) : "–"}</div>
      <div className="unrecognized-answer-row__task">
        {/* stretched-link roztáhne klik přes celý řádek, jméno odkazu zůstane jen název úlohy. [TD-334] */}
        {target ? (
          <Link to={target} state={{ from: RETURN_PATH }} className="stretched-link">
            {label}
          </Link>
        ) : (
          label
        )}
      </div>
      <div className="unrecognized-answer-row__answer unrecognized-answer-row__answer--student">
        <span className="unrecognized-answer-row__label">{t("mobile.student")}</span>
        <HighlightedAnswer segments={diff.student} />
      </div>
      <div className="unrecognized-answer-row__answer unrecognized-answer-row__answer--correct">
        <span className="unrecognized-answer-row__label">{t("mobile.correct")}</span>
        <HighlightedAnswer segments={diff.correct} />
      </div>
      <div className="unrecognized-answer-row__status">
        <AnswerStatusPill status={getAnswerStatus(checked, wasCorrect)} />
      </div>
    </div>
  );
};

export default UnrecognizedAnswerRow;
